/**
 * The third way into a game: somebody read you a code.
 *
 * It sits below the mode list rather than on a card, because joining genuinely names no mode. The
 * host decides what is being played and says so the moment the connection is up, so a join button
 * on a particular mode's card would be promising something this machine does not know yet.
 *
 * A code is checked here, before any network is touched. A typo is by far the most common way this
 * fails and the one failure that can be answered instantly and precisely, naming the character that
 * is wrong, rather than after six seconds of dialling relays for a room that cannot exist.
 *
 * It is one row in a titled box at the bottom of the menu, with nothing explaining it. Most people
 * who open this game are not holding a code somebody read them, and the one who is knows what to
 * do with it; the placeholder shows the shape.
 */

import { JOIN_ACTION, JOIN_FIELD_LABEL, JOIN_PLACEHOLDER } from '@/text/menuText';
import { el, setText, setVisible } from '@/ui/dom';
import { netErrorAdvice, netErrorMessage } from '@/net/session/sessionErrors';
import { parseJoinCode } from '@/net/session/joinCode';

export interface JoinEntry {
  readonly root: HTMLElement;
}

export function createJoinEntry(onJoin: (code: string) => void): JoinEntry {
  const root = el('section', 'menu-join');
  const field = el('input', 'menu-join-input');
  field.type = 'text';
  field.placeholder = JOIN_PLACEHOLDER;
  field.autocomplete = 'off';
  field.spellcheck = false;
  field.setAttribute('aria-label', JOIN_FIELD_LABEL);

  const button = el('button', 'menu-join-go', JOIN_ACTION);
  button.type = 'button';

  const problem = el('p', 'menu-join-problem');
  setVisible(problem, false);

  const attempt = (): void => {
    const parsed = parseJoinCode(field.value);
    if (!parsed.ok) {
      setText(problem, `${netErrorMessage('invalidCode')} ${advice(parsed.detail)}`);
      setVisible(problem, true);
      return;
    }
    setVisible(problem, false);
    onJoin(parsed.canonical);
  };

  button.addEventListener('click', attempt);
  field.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Enter') attempt();
  });
  field.addEventListener('input', () => {
    setVisible(problem, false);
  });

  const row = el('div', 'menu-join-row');
  row.append(field, button);

  root.append(row, problem);

  return { root };
}

/** The catalogue's wording, plus what the parser found. Neither one alone is enough to act on. */
function advice(detail: string): string {
  const general = netErrorAdvice('invalidCode') ?? '';
  return detail === '' ? general : `${detail.charAt(0).toUpperCase()}${detail.slice(1)}.`;
}
